import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Info, CheckIcon, Plus, X } from "lucide-react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { RootState } from "@/app/rootReducer";
import { useNotification } from "@/context/NotificationContext";
import {
  adminEditUserSchema,
  type AdminEditUserFormData,
} from "@/modules/hub/features/UserManagement/schemas/userSchemas";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { EntitySingleSelect } from "@/components/EntitySingleSelect/EntitySingleSelect";
import { fetchCompanies } from "@/api/fetchCompanies/fetchCompaniesApi";
import { fetchAdminUserFormDetails } from "../../api/fetchAdminUserFormDetails";
import { AdminUserFormDetails, PermissionOverride } from "../../api/fetchAdminUserFormDetails/types";
import { editAdminUser } from "../../api/editAdminUser";
import { fetchPermissionsForCompany, PermissionForDropdown } from "../../api/fetchPermissionsForCompany";
import { addPermissionOverride } from "../../api/addPermissionOverride";
import { removePermissionOverride } from "../../api/removePermissionOverride";

// Extended entity type for companies
interface CompanyEntity {
  id: string;
  name: string;
  isCertainPath: boolean;
}

const AdminEditUser: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [saveLoading, setSaveLoading] = useState(false);
  const userAppSettings = useSelector(
    (state: RootState) => state.userAppSettings.userAppSettings,
  );

  const [formDetails, setFormDetails] = useState<AdminUserFormDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<CompanyEntity | null>(null);

  // Permission override state
  const [permissionOverrides, setPermissionOverrides] = useState<PermissionOverride[]>([]);
  const [availablePermissions, setAvailablePermissions] = useState<PermissionForDropdown[]>([]);
  const [selectedPermissionId, setSelectedPermissionId] = useState<string>("");
  const [selectedOverrideType, setSelectedOverrideType] = useState<"grant" | "revoke">("grant");
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [overrideLoading, setOverrideLoading] = useState(false);

  // Check if user is editing their own account
  const isEditingSelf = userAppSettings?.employeeUuid === uuid;

  const form = useForm<AdminEditUserFormData>({
    resolver: zodResolver(adminEditUserSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      businessRoleId: undefined,
      companyUuid: "",
    },
    mode: "onChange",
  });

  const { control, setValue, handleSubmit, formState, watch } = form;
  const { isSubmitting, isValid } = formState;
  const selectedRoleId = watch("businessRoleId");

  useEffect(() => {
    const fetchFormDetails = async () => {
      if (!uuid) return;
      try {
        setLoading(true);
        const response = await fetchAdminUserFormDetails(uuid);
        setFormDetails(response.data);

        // Populate form with existing values
        if (response.data.firstName) {
          setValue("firstName", response.data.firstName);
        }
        if (response.data.lastName) {
          setValue("lastName", response.data.lastName);
        }
        if (response.data.email) {
          setValue("email", response.data.email);
        }
        if (response.data.employeeBusinessRoleId) {
          setValue("businessRoleId", response.data.employeeBusinessRoleId);
        }
        if (response.data.companyUuid && response.data.companyName !== null) {
          setValue("companyUuid", response.data.companyUuid);
          // Initialize selectedCompany from the response
          setSelectedCompany({
            id: response.data.companyUuid,
            name: response.data.companyName,
            isCertainPath: response.data.companyIsCertainPath || false,
          });
        }

        // Load existing permission overrides
        if (response.data.employeePermissionOverrides) {
          setPermissionOverrides(response.data.employeePermissionOverrides);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load user details");
      } finally {
        setLoading(false);
      }
    };
    fetchFormDetails();
  }, [uuid, setValue]);

  // Fetch available permissions when company changes
  useEffect(() => {
    const loadPermissions = async () => {
      if (!selectedCompany) {
        setAvailablePermissions([]);
        return;
      }

      try {
        setPermissionsLoading(true);
        const response = await fetchPermissionsForCompany(selectedCompany.id);
        setAvailablePermissions(response.data.permissions);
      } catch (err) {
        console.error("Failed to load permissions:", err);
        setAvailablePermissions([]);
      } finally {
        setPermissionsLoading(false);
      }
    };

    loadPermissions();
  }, [selectedCompany]);

  // Handle adding a permission override
  const handleAddOverride = async () => {
    if (!uuid || !selectedPermissionId) return;

    try {
      setOverrideLoading(true);
      const response = await addPermissionOverride(uuid, {
        permissionId: parseInt(selectedPermissionId, 10),
        overrideType: selectedOverrideType,
      });

      // Update local state - replace if exists, add if new
      setPermissionOverrides((prev) => {
        const existing = prev.findIndex((o) => o.permissionId === response.data.override.permissionId);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = response.data.override;
          return updated;
        }
        return [...prev, response.data.override];
      });

      // Reset selection
      setSelectedPermissionId("");
      setSelectedOverrideType("grant");

      showNotification("Permission override added", "", "success");
    } catch {
      // Error handled by axios interceptor
    } finally {
      setOverrideLoading(false);
    }
  };

  // Handle removing a permission override
  const handleRemoveOverride = async (overrideId: number) => {
    if (!uuid) return;

    try {
      setOverrideLoading(true);
      await removePermissionOverride(uuid, overrideId);

      // Update local state
      setPermissionOverrides((prev) => prev.filter((o) => o.id !== overrideId));

      showNotification("Permission override removed", "", "success");
    } catch {
      // Error handled by axios interceptor
    } finally {
      setOverrideLoading(false);
    }
  };

  const onSubmit = async (values: AdminEditUserFormData) => {
    if (uuid) {
      try {
        setSaveLoading(true);
        await editAdminUser(uuid, {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          businessRoleId: values.businessRoleId,
          companyUuid: values.companyUuid,
        });
        showNotification(
          "Successfully updated user!",
          "The user information has been updated.",
          "success",
        );
        navigate("/admin/users");
      } catch {
        // Error is handled by axios interceptor
      } finally {
        setSaveLoading(false);
      }
    }
  };

  const availableRoles = formDetails?.availableRoles || [];

  // Filter roles based on selected company
  // If CertainPath company → only show CP-only roles
  // If non-CP company → only show non-CP-only roles
  const filteredRoles = useMemo(() => {
    if (!selectedCompany) return [];
    if (selectedCompany.isCertainPath) {
      return availableRoles.filter((role) => role.isCertainPathOnly);
    }
    return availableRoles.filter((role) => !role.isCertainPathOnly);
  }, [availableRoles, selectedCompany]);

  const selectedRole = filteredRoles.find((r) => r.id === selectedRoleId) || null;

  // Clear role selection if it's no longer valid for the selected company
  useEffect(() => {
    if (selectedRoleId && !filteredRoles.find((r) => r.id === selectedRoleId)) {
      setValue("businessRoleId", undefined as unknown as number);
    }
  }, [filteredRoles, selectedRoleId, setValue]);

  // Fetch companies for EntitySingleSelect - use 100 per page like CompanySwitcherModal
  const fetchCompaniesForSelect = useCallback(
    async ({ searchTerm, page }: { searchTerm?: string; page?: number; pageSize?: number }) => {
      const result = await fetchCompanies({
        searchTerm,
        page,
        pageSize: 100,
        sortBy: "companyName",
        sortOrder: "ASC",
      });
      const { companies } = result.data;
      const totalCount = result.meta?.totalCount || companies.length;
      return {
        data: companies.map((c) => ({
          id: c.uuid,
          name: c.companyName,
          isCertainPath: c.isCertainPath || false,
        })),
        totalCount,
      };
    },
    [],
  );

  // Handle company selection
  const handleCompanyChange = useCallback(
    (company: CompanyEntity | null) => {
      setSelectedCompany(company);
      setValue("companyUuid", company?.id || "", { shouldValidate: true });
    },
    [setValue],
  );

  // Custom row renderer to show CP badge
  const renderCompanyRow = useCallback(
    (company: CompanyEntity, isSelected: boolean, toggle: (ent: CompanyEntity) => void) => (
      <div
        className={`cursor-pointer flex items-center gap-4 py-4 px-2 border-b last:border-0 ${isSelected ? "bg-blue-50" : ""}`}
        onClick={() => toggle(company)}
      >
        <div className="flex-1 flex items-center gap-2">
          <p className="font-medium">{company.name}</p>
          {company.isCertainPath && (
            <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full">
              CP
            </span>
          )}
        </div>
        {isSelected && <CheckIcon className="h-5 w-5 text-primary flex-shrink-0" />}
      </div>
    ),
    [],
  );

  if (!uuid) {
    return null;
  }

  return (
    <MainPageWrapper
      error={error}
      loading={loading || saveLoading}
      title="Edit User"
    >
      <Form {...form}>
        <form className="space-y-6 bg-white" onSubmit={handleSubmit(onSubmit)}>
          <div className="text-sm text-muted-foreground mb-2">
            Fields marked with an asterisk (*) are required.
          </div>
          <FormField
            control={control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Enter first name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Enter last name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email *</FormLabel>
                <FormControl>
                  <Input placeholder="Enter email address" type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="companyUuid"
            render={() => (
              <FormItem>
                <FormLabel>Company *</FormLabel>
                <FormControl>
                  <EntitySingleSelect<CompanyEntity>
                    entityNamePlural="Companies"
                    entityNameSingular="Company"
                    fetchEntities={fetchCompaniesForSelect}
                    onChange={handleCompanyChange}
                    placeholder="Select a company..."
                    renderEntityRow={renderCompanyRow}
                    value={selectedCompany}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="businessRoleId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Business Role *</FormLabel>
                <Select
                  disabled={isEditingSelf || !selectedCompany}
                  onValueChange={(value) => {
                    const parsed = parseInt(value, 10);
                    if (!isNaN(parsed)) {
                      field.onChange(parsed);
                    }
                  }}
                  value={field.value ? field.value.toString() : undefined}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={selectedCompany ? "Select a role..." : "Select a company first..."} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {filteredRoles.map((role) => (
                      <SelectItem key={role.id} value={role.id.toString()}>
                        {role.label}
                        {role.isCertainPathOnly && (
                          <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                            CP
                          </span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isEditingSelf && (
                  <div className="flex items-start gap-2 mt-2 p-3 bg-yellow-50 rounded-md border border-yellow-200">
                    <Info className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
                    <p className="text-sm text-yellow-800">
                      You cannot change your own role. To prevent locking yourself out, ask another administrator to update your role.
                    </p>
                  </div>
                )}
                {!isEditingSelf && selectedRole?.description && (
                  <div className="flex items-start gap-2 mt-2 p-3 bg-slate-50 rounded-md border border-slate-200">
                    <Info className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
                    <p className="text-sm text-slate-600">{selectedRole.description}</p>
                  </div>
                )}
                {selectedRole?.permissions && selectedRole.permissions.length > 0 && (
                  <div className="mt-3 p-3 bg-slate-50 rounded-md border border-slate-200">
                    <p className="text-sm font-medium text-slate-700 mb-2">This role includes:</p>
                    <ul className="text-sm text-slate-600 space-y-1">
                      {selectedRole.permissions.map((permission) => (
                        <li key={permission.id} className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                          {permission.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Permission Overrides Section */}
          {selectedCompany && (
            <div className="border rounded-lg p-4 space-y-4">
              <div>
                <h3 className="font-medium text-sm">Permission Overrides</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Grant or revoke specific permissions for this user beyond their role defaults.
                </p>
              </div>

              {/* Add new override */}
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="text-sm font-medium mb-1 block">Permission</label>
                  <Select
                    disabled={permissionsLoading || overrideLoading}
                    onValueChange={setSelectedPermissionId}
                    value={selectedPermissionId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={permissionsLoading ? "Loading..." : "Select a permission..."} />
                    </SelectTrigger>
                    <SelectContent>
                      {availablePermissions.map((permission) => (
                        <SelectItem key={permission.id} value={permission.id.toString()}>
                          {permission.label}
                          {permission.isCertainPathOnly && (
                            <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                              CP
                            </span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-32">
                  <label className="text-sm font-medium mb-1 block">Type</label>
                  <Select
                    disabled={overrideLoading}
                    onValueChange={(value) => setSelectedOverrideType(value as "grant" | "revoke")}
                    value={selectedOverrideType}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="grant">Grant</SelectItem>
                      <SelectItem value="revoke">Revoke</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  disabled={!selectedPermissionId || overrideLoading}
                  onClick={handleAddOverride}
                  size="icon"
                  type="button"
                  variant="outline"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {/* Current overrides list */}
              {permissionOverrides.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Current Overrides</label>
                  <div className="border rounded-md divide-y">
                    {permissionOverrides.map((override) => (
                      <div
                        key={override.id}
                        className="flex items-center justify-between px-3 py-2"
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full ${
                              override.overrideType === "grant"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {override.overrideType}
                          </span>
                          <span className="text-sm">{override.permissionLabel}</span>
                          <span className="text-xs text-muted-foreground">
                            ({override.permissionInternalName})
                          </span>
                        </div>
                        <Button
                          disabled={overrideLoading}
                          onClick={() => handleRemoveOverride(override.id)}
                          size="icon"
                          type="button"
                          variant="ghost"
                        >
                          <X className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {permissionOverrides.length === 0 && (
                <p className="text-sm text-muted-foreground italic">
                  No permission overrides configured.
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-4 pt-4">
            <Button onClick={() => navigate("/admin/users")} type="button" variant="outline">
              Cancel
            </Button>
            <Button disabled={!isValid || isSubmitting || saveLoading} type="submit">
              {isSubmitting || saveLoading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </Form>
    </MainPageWrapper>
  );
};

export default AdminEditUser;
